import {Location} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {ActivatedRoute, Router} from '@angular/router';
import {cloneDeep, flatten, isEmpty, isEqual} from 'lodash';
import {PortfolioService} from '../../service/portfolio.service';
import {PreferredCategories} from '../../model/portfolio-preferences.model';
import {PortfolioPreferenceResolverData} from './portfolio-preference.resolver';
import {ConfirmPopUpComponent} from '../../comfirm-pop-up/confirm-pop-up.component';
import {ManualInvestPortfolioCreationData} from '../../model/portfolio-creation-data/manual-invest-portfolio-creation-data.model';

@Component({
    selector: 'app-portfolio-preference',
    templateUrl: './portfolio-preference.component.html',
    styleUrls: ['./portfolio-preference.component.css']
})
export class PortfolioPreferenceComponent implements OnInit {
    isCustomPreferenceExpanded = false;
    initialPreferredCategories = [];
    preferredCategories: PreferredCategories[] = [];
    userOptedInvestmentDetail: { investmentAmount: number, numberOfLoansSelected: number, pendingAmountToInvest: number };
    private portfolioCreationData: ManualInvestPortfolioCreationData;
    private readonly router: Router;
    private readonly portfolioService: PortfolioService;

    constructor(private readonly activatedRoute: ActivatedRoute, portfolioService: PortfolioService, public readonly dialog: MatDialog,
                private readonly location: Location, router: Router) {
        this.router = router;
        this.portfolioService = portfolioService;
    }

    ngOnInit(): void {
        this.activatedRoute.data.subscribe(({investDetail, portfolioCreationData}: {
            investDetail: PortfolioPreferenceResolverData,
            portfolioCreationData: ManualInvestPortfolioCreationData
        }) => {
            this.userOptedInvestmentDetail = investDetail;
            this.initialPreferredCategories = investDetail.defaultPreferredCategories;
            const preferredCategories = investDetail.preferredCategories;
            this.onPreferredCategoriesReceived(preferredCategories);
            this.isCustomPreferenceExpanded = !isEmpty(preferredCategories);
            this.onPortfolioCreationDataReceived(portfolioCreationData);
        });
    }

    onPreferredCategoriesReceived(preferredCategories: PreferredCategories[]) {
        if (preferredCategories.length > 0) {
            this.preferredCategories = preferredCategories;
        } else {
            this.preferredCategories = cloneDeep(this.initialPreferredCategories);
        }
    }

    async saveCustomPortfolioPreference(): Promise<void> {
        const manualInvestPortfolioCreationData = this.portfolioCreationData;
        manualInvestPortfolioCreationData.portfolioPreference = this.preferredCategories;
        await this.portfolioService.createOrUpdateLocalPortfolioCreationData(manualInvestPortfolioCreationData);

        this.openManualInvestSummaryPage();
    }

    async saveFinzyProPreference() {
        if (this.isPreferenceModified()) {
            this.dialog.open(ConfirmPopUpComponent, {
                data: {
                    title: 'Discard preferences and continue with finzyPRO+?',
                    message: 'We noticed that you had selected some custom preferences but have chosen to go ahead with finzyPRO+. If you wish to use customer preferences click on cancel.',
                    positiveButtonText: 'CONTINUE WITH finzyPRO+',
                    negativeButtonText: 'CANCEL'
                },
                width: '50%',
                minWidth: '320px'
            }).afterClosed().subscribe(result => this.onConfirmationResultReceived(result));
        } else {
            await this.setMasterPortfolioPreference();
            this.openManualInvestSummaryPage();
        }
    }

    async onConfirmationResultReceived(result: boolean): Promise<void> {
        if (result) {
            await this.setMasterPortfolioPreference();
            this.openManualInvestSummaryPage();
        }
    }

    isPreferenceModified(): boolean {
        const preferredCategories = new Set(flatten(this.preferredCategories.map(category => category.ratingList)));
        const initialPreferredCategories = new Set(flatten(this.initialPreferredCategories.map(category => category.ratingList)));
        return !isEqual(preferredCategories, initialPreferredCategories);
    }

    isValidPreferenceSelection(): boolean {
        return (this.preferredCategories.length !== 0);
    }

    onSetPreferenceClick() {
        this.isCustomPreferenceExpanded = !this.isCustomPreferenceExpanded;
    }

    areInvestmentsAvailable() {
        return this.userOptedInvestmentDetail.numberOfLoansSelected > 0;
    }

    getWaitTime(preferredCategories: PreferredCategories[]): string {
        const waitTimes = ['20 days', '15 days', '10 days'];
        const preferredRatings = flatten(preferredCategories.map(cat => cat.ratingList));
        return waitTimes[preferredRatings.length / 3 - 1];
    }

    public backToPreviousPage() {
        this.router.navigate(['../'], {relativeTo: this.activatedRoute});
    }

    public onPortfolioCreationDataReceived(portfolioCreationData: ManualInvestPortfolioCreationData): void {
        this.portfolioCreationData = portfolioCreationData;
    }

    private async setMasterPortfolioPreference(): Promise<void> {
        const manualInvestPortfolioCreationData = this.portfolioCreationData;
        manualInvestPortfolioCreationData.portfolioPreference = this.initialPreferredCategories;
        await this.portfolioService.createOrUpdateLocalPortfolioCreationData(manualInvestPortfolioCreationData);
    }

    private openManualInvestSummaryPage() {
        this.router.navigate(['../portfolio-summary'], {relativeTo: this.activatedRoute});
    }
}
